import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Header, BottomNav, SkeletonList } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { useDate } from '../context/DateContext';
import { cleaningApi, CleaningItem } from '../api/client';
import { formatRoomAddress } from '../utils';
import { FiLock, FiUnlock } from 'react-icons/fi';

export const RoomSelectPage = () => {
  const { user } = useAuth();
  const { selectedDate } = useDate();
  const queryClient = useQueryClient();

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  const { data, isLoading, error } = useQuery({
    queryKey: ['cleaning-list', dateStr],
    queryFn: () => cleaningApi.getList(dateStr),
    refetchInterval: 5000, // 5초마다 자동 갱신
  });

  const toggleRoomMutation = useMutation({
    mutationFn: (listId: number) => cleaningApi.toggleRoom(listId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['cleaning-list'] });
      if (response.data.success) {
        toast.success(response.data.message || '객실 선택이 변경되었습니다.');
      } else {
        // 서버에서 실패 응답 (이미 다른 사람이 선택한 경우)
        toast.error(response.data.message || '객실을 선택할 수 없습니다.');
      }
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['cleaning-list'] });
      toast.error('작업에 실패했습니다.');
    },
  });

  const toggleUnlockMutation = useMutation({
    mutationFn: () => cleaningApi.toggleUnlock(dateStr),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['cleaning-list'] });
      if (response.data.success) {
        const unlocked = response.data.data?.unlocked;
        toast.success(unlocked ? '락이 해제되었습니다.' : '락이 설정되었습니다.');
      }
    },
    onError: () => {
      toast.error('락 상태 변경에 실패했습니다.');
    },
  });

  const handleRoomClick = (item: CleaningItem) => {
    if (item.canceled) {
      toast.error('취소된 객실입니다.');
      return;
    }
    if (item.completed) {
      toast.error('이미 완료된 객실입니다.');
      return;
    }
    if (item.userId && item.userId !== user?.id) {
      toast.error('이미 다른 사용자가 선택한 객실입니다.');
      return;
    }
    toggleRoomMutation.mutate(item.id);
  };

  const stats = data?.data.data?.stats;
  const rawList = data?.data.data?.list || [];
  const unlockStatus = data?.data.data?.unlockStatus || false;

  // 레벨 3은 다른 사람이 선택한 객실 숨김, 레벨 1,2는 모두 표시
  const isStaff = user?.level === 3;
  const isManager = user?.level === 1 || user?.level === 2;
  const filteredList = isStaff
    ? rawList.filter((item: CleaningItem) => !item.userId || item.userId === user?.id)
    : rawList;

  // 객실 정렬 (A동 → B동, 호수 오름차순)
  const list = [...filteredList].sort((a: CleaningItem, b: CleaningItem) => {
    const addrA = (a.roomInfo?.room_address || a.roomId || '').toUpperCase();
    const addrB = (b.roomInfo?.room_address || b.roomId || '').toUpperCase();

    // 동(첫 글자)과 호수 분리
    const buildingA = addrA.charAt(0);
    const buildingB = addrB.charAt(0);
    const roomNumA = addrA.slice(1).padStart(4, '0');
    const roomNumB = addrB.slice(1).padStart(4, '0');

    // 동 먼저 비교, 같으면 호수 비교
    if (buildingA !== buildingB) {
      return buildingA.localeCompare(buildingB);
    }
    return roomNumA.localeCompare(roomNumB);
  });

  // 상태별 카운트
  const myRooms = list.filter((item: CleaningItem) => item.userId === user?.id);
  const availableRooms = list.filter((item: CleaningItem) => !item.userId && !item.canceled);

  if (error) {
    return (
      <div className="app-container">
        <Header
          showDatePicker
          showProfile
          userName={user?.name}
        />
        <main className="main-content">
          <div className="text-center text-danger py-5">
            데이터를 불러오는데 실패했습니다.
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="app-container">
      <Header
        showDatePicker
        showProfile
        userName={user?.name}
      />

      <main className="main-content">
        {/* 상태 요약 - 레벨 1, 2만 표시 */}
        {isManager && (
          <div className="bg-white rounded-3 p-3 mb-3 shadow-sm">
            <div className="d-flex justify-content-around text-center">
              <div>
                <div className="text-muted small">전체</div>
                <div className="fw-bold fs-5">{stats?.total || 0}</div>
              </div>
              <div>
                <div className="text-muted small">선택가능</div>
                <div className="fw-bold fs-5 text-primary">{availableRooms.length}</div>
              </div>
              <div>
                <div className="text-muted small">내 객실</div>
                <div className="fw-bold fs-5 text-success">{myRooms.length}</div>
              </div>
              <div>
                <div className="text-muted small">완료</div>
                <div className="fw-bold fs-5">{stats?.completed || 0}</div>
              </div>
            </div>
          </div>
        )}

        {/* 락 해제 체크박스 - 레벨 1, 2만 표시 */}
        {isManager && (
          <div className="bg-white rounded-3 p-3 mb-3 shadow-sm">
            <div className="form-check d-flex align-items-center">
              <input
                className="form-check-input me-2"
                type="checkbox"
                id="unlockCheckbox"
                checked={unlockStatus}
                onChange={() => toggleUnlockMutation.mutate()}
                disabled={toggleUnlockMutation.isPending}
              />
              <label className="form-check-label d-flex align-items-center" htmlFor="unlockCheckbox">
                {unlockStatus ? (
                  <FiUnlock className="me-2 text-success" />
                ) : (
                  <FiLock className="me-2 text-danger" />
                )}
                청소 리스트 락 해제 (관리자 전용)
              </label>
            </div>
          </div>
        )}

        {/* 레벨 3이고 락이 걸려있을 때 잠김 메시지 */}
        {isStaff && !unlockStatus ? (
          <div className="text-center py-5">
            <FiLock size={48} className="text-muted mb-3" />
            <h5 className="text-muted">청소 리스트가 잠겨 있습니다</h5>
            <p className="text-muted small">관리자가 청소 리스트의 락을 해제하면 객실을 선택할 수 있습니다.</p>
          </div>
        ) : (
          <>
            {/* 안내 메시지 */}
            <div className="alert alert-info py-2 mb-3">
              <small>객실을 탭하여 선택하세요. 선택한 객실은 '내 청소' 탭에서 관리합니다.</small>
            </div>

            {/* 객실 목록 */}
            {isLoading ? (
              <SkeletonList count={6} />
            ) : (
              <div className="room-cards-grid">
                {list.map((item: CleaningItem) => {
                  const isMyRoom = item.userId === user?.id;
                  const isAvailable = !item.userId && !item.canceled && !item.completed;

                  let cardClass = 'room-card';
                  if (item.completed) cardClass += ' completed';
                  if (item.canceled) cardClass += ' canceled';
                  if (isMyRoom) cardClass += ' selected';

                  return (
                    <div
                      key={item.id}
                      className={cardClass}
                      onClick={() => handleRoomClick(item)}
                      style={{
                        opacity: (item.canceled || item.completed) ? 0.5 : 1,
                        cursor: isAvailable || isMyRoom ? 'pointer' : 'not-allowed'
                      }}
                    >
                      <div className="room-address">
                        {formatRoomAddress(item.roomInfo?.room_address) || item.roomId}
                        {item.isJaesil && (
                          <span className="badge bg-warning text-dark ms-1" style={{fontSize: '10px'}}>
                            재실
                          </span>
                        )}
                      </div>

                      <div className="room-user">
                        {item.userId ? (
                          <span className={isMyRoom ? 'text-primary fw-bold' : 'text-muted'}>
                            {item.userName || item.userId}
                            {isMyRoom && ' (나)'}
                          </span>
                        ) : (
                          <span className="text-muted">미배정</span>
                        )}
                      </div>

                      <div className="room-status">
                        {item.canceled ? (
                          <span className="status-badge bg-danger text-white">
                            {item.noshow ? '노쇼' : '취소'}
                          </span>
                        ) : item.completed ? (
                          <span className="status-badge bg-success text-white">
                            완료
                          </span>
                        ) : isMyRoom ? (
                          <span className="status-badge bg-primary text-white">
                            선택됨
                          </span>
                        ) : isAvailable ? (
                          <span className="status-badge bg-secondary text-white">
                            선택가능
                          </span>
                        ) : (
                          <span className="status-badge bg-secondary text-white">
                            진행중
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!isLoading && list.length === 0 && (
              <div className="text-center text-muted py-5">
                이 날짜에 청소 목록이 없습니다.
              </div>
            )}
          </>
        )}
      </main>

      <BottomNav />
    </div>
  );
};
